import { Box, Stack } from '@mui/material';
import { getRequestEvaluations } from 'lib/api/decisionEngine';
import type { EvaluationNode } from 'lib/api/decisionEngine/types';
import { formatDate, getChileUtcOffset } from 'lib/functions/utils';
import {
  BackBubbleButton,
  ContentCard,
  ErrorState,
  getStatusChipColor,
  HeroHeader,
  IconTitleCard,
  IconTitleCardSkeleton,
  MobileBody,
  MobileCarouselTabs,
  StatusChip,
} from 'lib/mobileDesignSystem';
import type { Evaluation } from 'lib/types/models';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { getEvaluationStatusColor } from '../utils/evaluationStatus';

const NODES_SKELETON_COUNT = 3;
const CHILE_OFFSET_MS = getChileUtcOffset() * 60 * 60 * 1000;

export type EvaluationNodesProduct = 'factoring' | 'confirming' | 'creditLine';

export interface SharedEvaluationNodesPageProps {
  product: EvaluationNodesProduct;
  quoteId?: number | null;
}

interface LocationState {
  evaluation?: Evaluation;
  quoteId?: number;
}

const NodesContent: React.FC<{
  nodes: EvaluationNode[];
  createdAt?: number;
}> = ({ nodes, createdAt }) => {
  if (nodes.length === 0) {
    return (
      <Stack spacing={2}>
        {createdAt != null && (
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <MobileBody color="text.secondary">
              {formatDate(new Date(createdAt + CHILE_OFFSET_MS), 'large')}
            </MobileBody>
          </Box>
        )}
        <MobileBody color="text.secondary">
          No hay nodos para esta evaluación.
        </MobileBody>
      </Stack>
    );
  }
  return (
    <Stack spacing={2}>
      {createdAt != null && (
        <Box sx={{ textAlign: 'center', py: 1 }}>
          <MobileBody color="text.secondary">
            {formatDate(new Date(createdAt + CHILE_OFFSET_MS), 'large')}
          </MobileBody>
        </Box>
      )}
      {nodes.map((node, nodeIndex) => (
        <ContentCard key={`${node.nodeIdDecision}-${nodeIndex}`}>
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <StatusChip
                text={node.statusNode.name}
                type={getEvaluationStatusColor(node.statusNode.name)}
              />
            </Box>
            <Box>
              <IconTitleCard
                adornmentText={nodeIndex + 1}
                title={node.nodeName || 'Sin comentarios'}
                description={node.resultText || undefined}
                sx={{ px: 1.5, py: 1.25 }}
              />
            </Box>
          </Stack>
        </ContentCard>
      ))}
    </Stack>
  );
};

const SharedEvaluationNodesPage: React.FC<SharedEvaluationNodesPageProps> = ({
  product,
  quoteId: quoteIdFromProps,
}) => {
  const { id, evaluationId } = useParams<{
    id: string;
    evaluationId: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const state = location.state as LocationState | undefined;

  const requestIdNum = id != null && id !== '' ? Number(id) : null;

  const isFactoring = product === 'factoring';
  const quoteId = isFactoring
    ? (state?.quoteId ?? quoteIdFromProps ?? null)
    : null;
  const overviewLoaded = !isFactoring || quoteIdFromProps !== undefined;

  const factoringQuery1 = getRequestEvaluations(dispatch, {
    requestType: 'factoring-request',
    requestId: quoteId,
  });
  const factoringQuery2 = getRequestEvaluations(dispatch, {
    requestType: 'factoring-request-payers',
    requestId: requestIdNum,
  });
  const confirmingQuery = getRequestEvaluations(dispatch, {
    requestType: 'confirming-request',
    requestId: requestIdNum,
  });
  const creditLineQuery = getRequestEvaluations(dispatch, {
    requestType: 'creditLine',
    requestId: requestIdNum,
  });

  const allGroups = useMemo(() => {
    if (product === 'factoring') {
      const g0 = Object.values(factoringQuery1.data ?? {});
      const g1 = Object.values(factoringQuery2.data ?? {});
      return [...g0, ...g1];
    }
    if (product === 'confirming') {
      return Object.values(confirmingQuery.data ?? {});
    }
    return Object.values(creditLineQuery.data ?? {});
  }, [
    product,
    factoringQuery1.data,
    factoringQuery2.data,
    confirmingQuery.data,
    creditLineQuery.data,
  ]);

  const evaluationFromState = state?.evaluation;

  const versions: Evaluation[] = useMemo(() => {
    if (evaluationFromState != null) {
      const eid = evaluationFromState.id;
      const group = allGroups.find((g) => g.some((e) => e.id === eid));
      return group ?? [evaluationFromState];
    }
    const eid = evaluationId != null ? Number(evaluationId) : NaN;
    if (Number.isNaN(eid)) return [];
    const group = allGroups.find((g) => g.some((e) => e.id === eid));
    return group ?? [];
  }, [evaluationFromState, evaluationId, allGroups]);

  const latestEvaluation = versions[0] ?? null;

  const isPending =
    product === 'factoring'
      ? factoringQuery1.isPending || factoringQuery2.isPending
      : product === 'confirming'
        ? confirmingQuery.isPending
        : creditLineQuery.isPending;

  const isResolvingFromApi =
    versions.length === 0 &&
    (isPending || (isFactoring && !overviewLoaded && !!id));

  const title = latestEvaluation?.label ?? 'Nodos de evaluación';

  const carouselItems = useMemo(
    () =>
      versions.map((v) => {
        const nodes = v.result;
        return {
          value: String(v.id),
          label: v.reviewerName ?? 'Sin revisor',
          Component: () => (
            <NodesContent nodes={nodes} createdAt={v.createdAt} />
          ),
        };
      }),
    [versions],
  );

  const handleBack = () => navigate(-1);

  if (!id) {
    return <ErrorState message="Identificador de operación no válido." />;
  }

  if (
    isFactoring &&
    evaluationFromState == null &&
    overviewLoaded &&
    quoteId == null
  ) {
    return (
      <ErrorState message="No se encontró cotización para esta operación." />
    );
  }

  if (
    evaluationFromState == null &&
    !isPending &&
    versions.length === 0 &&
    (!isFactoring || overviewLoaded)
  ) {
    return (
      <ErrorState message="No se encontró la evaluación o no tiene nodos." />
    );
  }

  return (
    <Stack sx={{ minHeight: '100%', pb: 4, px: 0 }} spacing={0}>
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <HeroHeader
          title={title}
          minHeight={60}
          fadeColor={getStatusChipColor('info')}
          sx={{ width: '100vw', mx: 'calc(-50vw + 50%)' }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            pt: 0.5,
            px: 2,
          }}
        >
          <BackBubbleButton onClick={handleBack} />
        </Box>
      </Box>

      <Stack spacing={2} sx={{ px: 2, pt: 2, flex: 1 }}>
        {isResolvingFromApi ? (
          <Stack spacing={2}>
            {Array.from({ length: NODES_SKELETON_COUNT }, (_, i) => (
              <ContentCard key={`skeleton-${i}`}>
                <Stack spacing={1.5}>
                  <IconTitleCardSkeleton />
                </Stack>
              </ContentCard>
            ))}
          </Stack>
        ) : carouselItems.length === 0 ? (
          <MobileBody color="text.secondary">
            No se pudo cargar la evaluación.
          </MobileBody>
        ) : (
          <MobileCarouselTabs items={carouselItems} />
        )}
      </Stack>
    </Stack>
  );
};

export default SharedEvaluationNodesPage;
